import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  mockOrders,
  mockIncomingOrders,
  type Order,
  type OrderStatus,
  type OrderItem,
  type PickupMethod,
  type PaymentMethod,
} from "@/mocks/branch/orders";
import { ORDER_STATUS, shouldRecordDebt, statusLabelOf, CUSTOMER_DEBT_AMOUNT } from "@/pages/branch/orders/constants";
import { mockCustomerWallets } from "@/mocks/branch/wallets";

export type ConnectionState = "connected" | "lost" | "reconnecting";

export interface Toast {
  id: string;
  type: "success" | "info" | "error";
  message: string;
}

export interface CreatePosOrderInput {
  pickupMethod: PickupMethod;
  paymentMethod: PaymentMethod;
  notes?: string;
  items: OrderItem[];
  subtotal: number;
  total: number;
}

interface OrdersContextValue {
  orders: Order[];
  loading: boolean;
  error: string | null;
  connection: ConnectionState;
  unreadNumbers: number[];
  toasts: Toast[];
  selectedOrder: Order | null;
  openOrderDetail: (id: string) => void;
  openOrderByNumber: (number: number) => void;
  closeOrderDetail: () => void;
  updateStatus: (id: string, status: OrderStatus) => void;
  confirmNotPicked: (id: string) => void;
  rateCustomer: (id: string, rating: number, byName: string) => void;
  createPosOrder: (data: CreatePosOrderInput) => Order;
  dismissToast: (id: string) => void;
  retry: () => void;
  simulateError: () => void;
  simulateDisconnect: () => void;
}

const OrdersContext = createContext<OrdersContextValue | undefined>(undefined);

let toastSeq = 0;

export function OrdersProvider({ children }: { children: ReactNode }) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionState>("connected");
  const [unreadNumbers, setUnreadNumbers] = useState<number[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [wallets, setWallets] = useState<Record<string, number>>(() =>
    Object.fromEntries(mockCustomerWallets.map((w) => [w.customerPhone, w.available])),
  );

  const incomingIndex = useRef(0);
  const nextNumber = useRef(1237);

  const addToast = useCallback((type: Toast["type"], message: string) => {
    const id = `toast-${++toastSeq}`;
    setToasts((prev) => [...prev, { id, type, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3600);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      setOrders(mockOrders);
      setLoading(false);
    }, 900);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const MAX_INCOMING = 2;
    let pushed = 0;
    let interval: number | undefined;

    const pushIncoming = () => {
      if (pushed >= MAX_INCOMING) {
        if (interval !== undefined) window.clearInterval(interval);
        return;
      }
      pushed += 1;
      const template = mockIncomingOrders[incomingIndex.current % mockIncomingOrders.length];
      incomingIndex.current += 1;
      const number = nextNumber.current;
      nextNumber.current += 1;
      const incoming: Order = {
        ...template,
        id: `o-${number}`,
        number,
        minutesAgo: 0,
        createdAt: "14:45",
      };
      setOrders((prev) => [incoming, ...prev]);
      setUnreadNumbers((prev) => [...prev, number]);
      addToast("info", `طلب جديد #${number}`);
      if (pushed >= MAX_INCOMING && interval !== undefined) {
        window.clearInterval(interval);
      }
    };

    const firstTimer = window.setTimeout(() => {
      pushIncoming();
      if (pushed < MAX_INCOMING) {
        interval = window.setInterval(pushIncoming, 25000);
      }
    }, 9000);

    return () => {
      window.clearTimeout(firstTimer);
      if (interval !== undefined) window.clearInterval(interval);
    };
  }, [addToast]);

  const updateStatus = useCallback(
    (id: string, status: OrderStatus) => {
      const target = orders.find((o) => o.id === id);
      const debtJustRecorded = Boolean(
        target && status === "preparing" && shouldRecordDebt(target) && !target.debtRecorded,
      );

      setOrders((prev) =>
        prev.map((o) => {
          if (o.id !== id) return o;
          const next: Order = { ...o, status };
          // عدّاد مهلة الاستلام خاص بالاستلام من الفرع/السيارة — لا ينطبق على التوصيل
          if (status === "ready" && o.pickupMethod !== "delivery") next.pickupRemainingSeconds = 900;
          // طلب التطبيق النقدي: تُصدر الفاتورة فقط عند الاستلام وتحصيل المبلغ
          if (status === "picked_up" && o.source === "app" && o.paymentMethod === "cash") {
            next.invoiceIssued = true;
          }
          if (status === "not_picked") {
            next.invoiceIssued = false;
          }
          if (debtJustRecorded) {
            next.debtRecorded = true;
          }
          return next;
        }),
      );

      if (target && target.status === "new") {
        setUnreadNumbers((prev) => prev.filter((n) => n !== target.number));
      }

      if (debtJustRecorded) {
        addToast(
          "success",
          `تم بدء التحضير وتسجيل رسوم استخدام المنصة بقيمة 0.80 ريال على المحل`,
        );
      } else {
        const label = target ? statusLabelOf(target, status) : ORDER_STATUS[status].label;
        addToast("success", `تم تحديث الحالة إلى «${label}»`);
      }
    },
    [orders, addToast],
  );

  /**
   * تأكيد عدم استلام العميل لطلب الكاش (بعد انتهاء المهلة فقط) — إجراء يدوي من موظف الفرع.
   * يحوّل الطلب لـ«لم يُستلم»، يسجّل المديونية، يخصم المتاح من المحفظة،
   * ويسجّل المتبقي كرصيد سالب مع إيقاف العميل عن إنشاء طلبات جديدة.
   */
  const confirmNotPicked = useCallback(
    (id: string) => {
      const target = orders.find((o) => o.id === id);
      if (!target || target.status === "not_picked") return;

      const debt = CUSTOMER_DEBT_AMOUNT;
      const phone = target.customerPhone ?? "";
      const available = phone ? wallets[phone] ?? 0 : 0;
      const deducted = Math.min(available, debt);
      const shortfall = Math.max(0, debt - deducted);

      if (phone && deducted > 0) {
        setWallets((prev) => ({ ...prev, [phone]: Math.max(0, (prev[phone] ?? 0) - deducted) }));
      }

      setOrders((prev) =>
        prev.map((o) =>
          o.id === id
            ? {
                ...o,
                status: "not_picked",
                invoiceIssued: false,
                // طلب كاش لم يُستلم: لا تُسجَّل رسوم التطبيق كمديونية على المحل
                // (العميل لم يستلم ولم يدفع نقدًا)، وتُحتسب فقط بعد تحصيل المبلغ منه.
                debtRecorded: false,
                customerDebtAmount: debt,
                walletDeducted: deducted,
                walletShortfall: shortfall,
                customerBlocked: true,
              }
            : o,
        ),
      );

      addToast(
        "success",
        `تم تأكيد عدم الاستلام وتسجيل مديونية ${debt.toFixed(2)} ر.س على محفظة العميل`,
      );
    },
    [orders, wallets, addToast],
  );

  const openOrderDetail = useCallback(
    (id: string) => {
      setSelectedOrderId(id);
      const target = orders.find((o) => o.id === id);
      if (target) {
        setUnreadNumbers((prev) => prev.filter((n) => n !== target.number));
      }
    },
    [orders],
  );

  const openOrderByNumber = useCallback(
    (number: number) => {
      const target = orders.find((o) => o.number === number);
      if (target) {
        setSelectedOrderId(target.id);
        setUnreadNumbers((prev) => prev.filter((n) => n !== number));
      }
    },
    [orders],
  );

  const closeOrderDetail = useCallback(() => setSelectedOrderId(null), []);

  const rateCustomer = useCallback(
    (id: string, rating: number, byName: string) => {
      const now = new Date();
      const stamp = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      setOrders((prev) =>
        prev.map((o) =>
          o.id === id
            ? { ...o, staffRating: rating, staffRatedBy: byName, staffRatedAt: stamp }
            : o,
        ),
      );
      addToast("success", "تم تقييم العميل");
    },
    [addToast],
  );

  const createPosOrder = useCallback(
    (data: CreatePosOrderInput): Order => {
      const number = nextNumber.current;
      nextNumber.current += 1;
      const now = new Date();
      const createdAt = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const order: Order = {
        id: `o-${number}`,
        number,
        source: "pos",
        status: "preparing",
        pickupMethod: data.pickupMethod,
        paymentMethod: data.paymentMethod,
        customerName: "زبون محلي",
        notes: data.notes,
        items: data.items,
        subtotal: data.subtotal,
        discount: 0,
        appFee: 0,
        total: data.total,
        createdAt,
        minutesAgo: 0,
        invoiceIssued: true,
      };
      setOrders((prev) => [order, ...prev]);
      addToast("success", `تم إنشاء الطلب #${number} وإضافته لقائمة الطلبات`);
      return order;
    },
    [addToast],
  );

  const retry = useCallback(() => {
    setLoading(true);
    setError(null);
    setConnection("reconnecting");
    window.setTimeout(() => {
      setOrders(mockOrders);
      setLoading(false);
      setConnection("connected");
      addToast("success", "تم استعادة الاتصال");
    }, 1400);
  }, [addToast]);

  const simulateError = useCallback(() => {
    setError("تعذّر الاتصال بالخدمة — تحقق من الشبكة وأعد المحاولة");
    setLoading(false);
  }, []);

  const simulateDisconnect = useCallback(() => {
    setConnection("lost");
    window.setTimeout(() => setConnection("connected"), 4000);
  }, []);

  const selectedOrder = useMemo(
    () => orders.find((o) => o.id === selectedOrderId) ?? null,
    [orders, selectedOrderId],
  );

  const value = useMemo<OrdersContextValue>(
    () => ({
      orders,
      loading,
      error,
      connection,
      unreadNumbers,
      toasts,
      selectedOrder,
      openOrderDetail,
      openOrderByNumber,
      closeOrderDetail,
      updateStatus,
      confirmNotPicked,
      rateCustomer,
      createPosOrder,
      dismissToast,
      retry,
      simulateError,
      simulateDisconnect,
    }),
    [
      orders,
      loading,
      error,
      connection,
      unreadNumbers,
      toasts,
      selectedOrder,
      openOrderDetail,
      openOrderByNumber,
      closeOrderDetail,
      updateStatus,
      confirmNotPicked,
      rateCustomer,
      createPosOrder,
      dismissToast,
      retry,
      simulateError,
      simulateDisconnect,
    ],
  );

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
}

export function useOrders(): OrdersContextValue {
  const ctx = useContext(OrdersContext);
  if (!ctx) {
    throw new Error("useOrders must be used within an OrdersProvider");
  }
  return ctx;
}